/**
 * Side panel that frames the journey ahead so the user sees the
 * sign-up as the FIRST of several deliberate steps, not a dead-end
 * gatekeeper. Used by the AuthLayout right column.
 *
 * Three variants by `phase`:
 *   - 'sign_up'  → step 1 highlighted, the rest are previews
 *   - 'verify'   → step 2 highlighted ("verify your email")
 *   - 'sign_in'  → same steps but framed as "welcome back"
 *
 * The numerals use Fraunces — big, beautifully cut figures that
 * carry the editorial signature without leaning on decoration.
 */
import styles from './WhatsNextPanel.module.css';

type TJourneyPhase = 'sign_up' | 'verify' | 'sign_in';

interface IStep {
  number: number;
  title: string;
  detail: string;
  duration: string;
}

const STEPS: readonly IStep[] = [
  {
    number: 1,
    title: 'Create your account',
    detail: 'Save your screener answers so you can come back any time.',
    duration: '30 seconds',
  },
  {
    number: 2,
    title: 'Verify your email',
    detail: 'One click in the inbox — we use it to ship your packet.',
    duration: '1 minute',
  },
  {
    number: 3,
    title: 'Tell us about your case',
    detail: "Answer in plain English. We handle the paperwork.",
    duration: '~30 min, save as you go',
  },
  {
    number: 4,
    title: 'Our team reviews + assembles',
    detail: 'Human review on every case. We print and ship to you.',
    duration: 'Within 72 hours',
  },
  {
    number: 5,
    title: 'You sign + mail to USCIS',
    detail: 'Blue ink, the fees, and a tracking number.',
    duration: '15 min at home',
  },
];

const ACTIVE_STEP_BY_PHASE: Record<TJourneyPhase, number> = {
  sign_up: 1,
  verify: 2,
  sign_in: 1,
};

export interface IWhatsNextPanelProps {
  phase: TJourneyPhase;
}

export function WhatsNextPanel({ phase }: IWhatsNextPanelProps): JSX.Element {
  const activeStep = ACTIVE_STEP_BY_PHASE[phase];
  return (
    <div className={styles.panel} data-whats-next-phase={phase}>
      <p className={styles.eyebrow}>The journey ahead</p>
      <ol className={styles.steps}>
        {STEPS.map((step) => {
          const status =
            step.number === activeStep
              ? 'active'
              : step.number < activeStep
              ? 'done'
              : 'upcoming';
          return (
            <li
              key={step.number}
              className={`${styles.step} ${styles[`step--${status}`]}`}
              data-step-status={status}
            >
              <span className={styles.step__numeral} aria-hidden="true">
                {status === 'done' ? '✓' : step.number}
              </span>
              <div className={styles.step__body}>
                <div className={styles.step__title_row}>
                  <h3 className={styles.step__title}>{step.title}</h3>
                  <span className={styles.step__duration}>{step.duration}</span>
                </div>
                <p className={styles.step__detail}>{step.detail}</p>
              </div>
            </li>
          );
        })}
      </ol>

      <div className={styles.guarantee}>
        <p className={styles.guarantee__lede}>
          <em>Built by immigrants, for immigrants.</em>
        </p>
        <p className={styles.guarantee__body}>
          Every case is reviewed by a human before your packet ships. If
          we miss a deadline we promised, we refund the prep fee — no
          questions.
        </p>
      </div>
    </div>
  );
}
